import type { ConvexReactClient } from "convex/react";
import type { AuthStore } from "@/convex/auth";
import {
  readerIdOf,
  type SessionState,
  type SessionStore,
} from "@/convex/session";
import { clockSkewOf, type BookmarkSync } from "./bookmarks";
import type { FluencyStore } from "./fluency";
import type { OfflineReader } from "./offline-reader";
import type { Outbox } from "./outbox";

export const IDLE_CHECK_INTERVAL_MS = 10_000;
// The one trigger ../app did not have: a reader who stays on one page
// still syncs.
export const IDLE_FLUSH_AGE_MS = 30_000;
export const EXPIRY_CHECK_INTERVAL_MS = 60 * 60_000;

export interface SyncTriggerDeps {
  outbox: Outbox;
  bookmarks: BookmarkSync;
  fluency: FluencyStore;
  authStore: Pick<AuthStore, "getState" | "subscribe">;
  sessionStore: Pick<SessionStore, "getState" | "subscribe">;
  offlineReader?: Pick<OfflineReader, "note">;
  convex?: Pick<ConvexReactClient, "subscribeToConnectionState">;
  target?: Pick<Window, "addEventListener" | "removeEventListener">;
  doc?: Pick<Document, "addEventListener" | "removeEventListener"> & {
    readonly visibilityState: DocumentVisibilityState;
  };
}

/**
 * Hidden/pagehide, online, Convex reconnect, auth turning authenticated,
 * a reader switch and an idle timer flush; a reader switch and an hourly
 * timer expire queued rows. Page turns and the completion dialog call
 * `outbox.flush()` themselves. Returns the teardown.
 */
export function installSyncTriggers({
  outbox,
  bookmarks,
  fluency,
  authStore,
  sessionStore,
  offlineReader,
  convex,
  target = window,
  doc = document,
}: SyncTriggerDeps): () => void {
  const flushAll = () => {
    void outbox.flush();
    void bookmarks.flush();
  };
  const retryAll = () => {
    void outbox.retryNow();
    void bookmarks.retryNow();
  };

  const onVisibility = () => {
    if (doc.visibilityState === "hidden") flushAll();
    else void fluency.syncChangedSince();
  };
  doc.addEventListener("visibilitychange", onVisibility);
  target.addEventListener("pagehide", flushAll);
  target.addEventListener("online", retryAll);

  let authState = authStore.getState();
  const stopAuth = authStore.subscribe(() => {
    const next = authStore.getState();
    if (next === "authenticated" && authState !== "authenticated") {
      retryAll();
      void fluency.syncChangedSince();
    }
    authState = next;
  });

  const recordSkew = (state: SessionState) => {
    const skew = clockSkewOf(state);
    if (skew !== null) void bookmarks.setClockSkew(skew);
  };
  recordSkew(sessionStore.getState());
  offlineReader?.note(sessionStore.getState());
  let readerId = readerIdOf(sessionStore.getState());
  const stopSession = sessionStore.subscribe(() => {
    const state = sessionStore.getState();
    offlineReader?.note(state);
    recordSkew(state);
    const next = readerIdOf(state);
    if (next !== readerId) {
      const previous = readerId;
      void (async () => {
        if (previous !== null) await outbox.noteReaderSession(previous);
        if (next !== null) await outbox.expireQueuedRows();
      })();
    }
    if (next !== null && next !== readerId) {
      retryAll();
      void fluency.syncChangedSince();
    }
    readerId = next;
  });

  let connected = true;
  const stopConnection = convex?.subscribeToConnectionState((state) => {
    if (state.isWebSocketConnected && !connected) retryAll();
    connected = state.isWebSocketConnected;
  });

  const idle = setInterval(() => {
    void outbox.flushIfOlderThan(IDLE_FLUSH_AGE_MS);
  }, IDLE_CHECK_INTERVAL_MS);
  const expiry = setInterval(() => {
    void outbox.expireQueuedRows();
  }, EXPIRY_CHECK_INTERVAL_MS);

  flushAll();
  void fluency.syncChangedSince();

  return () => {
    doc.removeEventListener("visibilitychange", onVisibility);
    target.removeEventListener("pagehide", flushAll);
    target.removeEventListener("online", retryAll);
    stopAuth();
    stopSession();
    stopConnection?.();
    clearInterval(idle);
    clearInterval(expiry);
  };
}
